import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Copy, Share2, RefreshCw, FileText, X } from "lucide-react";
import { VerdictBadge } from "@/components/public/Badges";
import { AudioPlayer } from "@/components/public/AudioPlayer";
import { useI18n } from "@/lib/i18n";
import { useVerificationBySlug } from "@/hooks/useVerifications";
import { adaptVerification } from "@/lib/adapters";
import { useSeo, useJsonLd } from "@/hooks/useSeo";
import { SITE_URL, truncate } from "@/lib/seo";
import type { Multilingual } from "@/lib/mock-data";

// Empêche le clic droit / le glisser-déposer sur les pièces jointes (dissuasion, pas une protection absolue).
const noCopyProps = {
  onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  draggable: false,
} as const;

function AttachmentPreviewModal({
  attachment,
  onClose,
}: {
  attachment: { url: string; libelle?: string; kind: "image" | "document" };
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-background/90 p-2 text-foreground hover:bg-background"
        aria-label="Fermer"
      >
        <X className="h-5 w-5" />
      </button>
      <div
        className="max-h-[85vh] w-full max-w-4xl select-none"
        onClick={(e) => e.stopPropagation()}
        {...noCopyProps}
      >
        {attachment.kind === "image" ? (
          <img
            src={attachment.url}
            alt={attachment.libelle || ""}
            className="mx-auto max-h-[85vh] w-auto rounded-md object-contain"
            {...noCopyProps}
          />
        ) : (
          <iframe
            src={attachment.url}
            title={attachment.libelle || "Document"}
            className="h-[85vh] w-full rounded-md bg-white"
          />
        )}
        {attachment.libelle && (
          <p className="mt-2 text-center text-xs text-white/80">{attachment.libelle}</p>
        )}
      </div>
    </div>
  );
}

function FactCheckSkeleton() {
  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 py-10 md:py-14 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="h-6 w-20 rounded bg-muted" />
        <div className="h-4 w-24 rounded bg-muted" />
      </div>
      <div className="mt-4 space-y-3">
        <div className="h-7 rounded bg-muted" />
        <div className="h-7 w-4/5 rounded bg-muted" />
      </div>
      <div className="mt-5 space-y-2">
        <div className="h-4 rounded bg-muted" />
        <div className="h-4 rounded bg-muted" />
        <div className="h-4 w-2/3 rounded bg-muted" />
      </div>
      <div className="mt-10 space-y-8">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-3 w-36 rounded bg-muted" />
            <div className="h-4 rounded bg-muted" />
            <div className="h-4 w-3/4 rounded bg-muted" />
          </div>
        ))}
      </div>
    </article>
  );
}

function FactCheckDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { locale, t } = useI18n();
  const [preview, setPreview] = useState<{ url: string; libelle?: string; kind: "image" | "document" } | null>(null);

  const {
    data: verificationDto,
    isLoading,
    isError,
    refetch,
  } = useVerificationBySlug(slug!);

  // Calculées avant les retours anticipés : les hooks de SEO doivent s'exécuter
  // à chaque rendu (règle des Hooks), y compris pendant le chargement/l'erreur.
  const previewFc = verificationDto ? adaptVerification(verificationDto) : undefined;
  const previewGet = (m?: Multilingual) =>
    (m as Record<string, string | undefined> | undefined)?.[locale] ?? m?.fr ?? "";
  const previewClaim = previewFc ? previewGet(previewFc.claim) : "";
  const previewImage = previewFc?.attachments?.find((a) => a.type === "image")?.url;

  useSeo({
    title: previewClaim ? `« ${previewClaim} » — Ramba News` : "Ramba News",
    description: truncate(previewFc ? previewGet(previewFc.shortExplanation) || previewClaim : ""),
    path: `/factcheck/${slug}`,
    image: previewImage,
    type: "article",
  });

  useJsonLd("ld-breadcrumb", previewFc ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: locale === "fr" ? "Accueil" : "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Fact-check", item: `${SITE_URL}/factcheck` },
      { "@type": "ListItem", position: 3, name: previewClaim, item: `${SITE_URL}/factcheck/${slug}` },
    ],
  } : undefined);

  if (isLoading) return <FactCheckSkeleton />;

  if (isError || !verificationDto) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <p className="text-sm text-muted-foreground">{t("factcheck.notFound")}</p>
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-semibold hover:bg-surface transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" /> {t("cta.retry")}
          </button>
          <Link to="/factcheck" className="text-xs text-verified">{t("factcheck.backAll")}</Link>
        </div>
      </div>
    );
  }

  const fc = adaptVerification(verificationDto);
  const get = (m?: Multilingual) =>
    (m as Record<string, string | undefined> | undefined)?.[locale] ?? m?.fr ?? "";

  const sections = [
    { title: t("factcheck.section.circulating"), body: get(fc.whatIsCirculating) },
    { title: t("factcheck.section.verified"), body: get(fc.whatWeVerified) },
    { title: t("factcheck.section.evidence"), body: get(fc.evidence) },
    { title: t("factcheck.section.why"), body: get(fc.whyItMatters) },
  ].filter((s) => s.body);

  const attachments = fc.attachments ?? [];
  const images = attachments.filter((a) => a.type === "image");
  const videos = attachments.filter((a) => a.type === "video");
  const audios = attachments.filter((a) => a.type === "audio");
  const documents = attachments.filter((a) => !["image", "video", "audio"].includes(a.type));

  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 py-10 md:py-14">
      <Link to="/factcheck" className="text-xs text-muted-foreground hover:text-foreground">
        {t("factcheck.backAll")}
      </Link>

      <div className="mt-4 flex items-center gap-3">
        <VerdictBadge verdict={fc.verdict} size="md" />
        <span className="text-xs text-muted-foreground">
          {new Date(fc.date).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}
        </span>
      </div>

      <h1 className="mt-4 font-serif text-3xl md:text-4xl font-semibold leading-tight tracking-tight">
        « {get(fc.claim)} »
      </h1>

      <p className="mt-5 text-lg leading-relaxed text-foreground">{get(fc.shortExplanation)}</p>

      <div className="mt-10 space-y-8">
        {sections.map((s) => (
          <section key={s.title}>
            <h2 className="text-xs font-bold uppercase tracking-[0.16em] text-verified">{s.title}</h2>
            <p className="mt-2 text-base leading-relaxed text-foreground/90">{s.body}</p>
          </section>
        ))}
      </div>

      {attachments.length > 0 && (
        <section className="mt-10">
          <h2 className="text-xs font-bold uppercase tracking-[0.16em] text-verified">
            {t("factcheck.attachments.title")}
          </h2>
          <div className="mt-3 space-y-4">
            {images.length > 0 && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {images.map((img) => (
                  <button
                    key={img.id ?? img.url}
                    type="button"
                    onClick={() => setPreview({ url: img.url, libelle: img.libelle, kind: "image" })}
                    className="block overflow-hidden rounded-md border border-border select-none"
                    {...noCopyProps}
                  >
                    <img
                      src={img.url}
                      alt={img.libelle || ""}
                      className="h-32 w-full object-cover"
                      {...noCopyProps}
                    />
                  </button>
                ))}
              </div>
            )}

            {videos.map((v) => (
              <div key={v.id ?? v.url} className="space-y-1.5">
                <video controls src={v.url} className="w-full rounded-md border border-border" />
                {v.libelle && <p className="text-xs text-muted-foreground">{v.libelle}</p>}
              </div>
            ))}

            {audios.map((a) => (
              <AudioPlayer key={a.id ?? a.url} title={a.libelle} src={a.url} />
            ))}

            {documents.map((d) => (
              <button
                key={d.id ?? d.url}
                type="button"
                onClick={() => setPreview({ url: d.url, libelle: d.libelle, kind: "document" })}
                className="flex w-full items-center gap-3 rounded-md border border-border bg-surface p-3 text-left text-sm hover:bg-surface/70 transition-colors"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{d.libelle || t("factcheck.attachments.document")}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {preview && <AttachmentPreviewModal attachment={preview} onClose={() => setPreview(null)} />}

      {fc.safeShare && get(fc.safeShare) && (
        <div className="mt-10 rounded-md border border-verified/30 bg-verified/5 p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-verified">
            {t("factcheck.safeShareTitle")}
          </div>
          <p className="mt-2 text-sm text-foreground">{get(fc.safeShare)}</p>
          {/* <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <button className="inline-flex items-center gap-1.5 rounded-md bg-foreground px-3 py-1.5 text-background">
              <Share2 className="h-3.5 w-3.5" /> WhatsApp
            </button>
            <button className="inline-flex items-center gap-1.5 rounded-md bg-foreground px-3 py-1.5 text-background">
              <Share2 className="h-3.5 w-3.5" /> Telegram
            </button>
            <button className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5">
              <Copy className="h-3.5 w-3.5" /> {t("cta.copy")}
            </button>
          </div> */}
        </div>
      )}
    </article>
  );
}

export default FactCheckDetail;
